import { AppState, AppStateStatus } from 'react-native';
import { useEffect, useRef } from 'react';
import { useNavigation } from '@react-navigation/native';

export enum FocusSource {
  App,
  Navigation,
}

interface Options {
  initialFocus?: boolean;
  // Wrap these callbacks in `useCallback` so the references don't change
  // and cause unexpected executions
  onBeforeRemove?: () => void;
  onBlur?: (type: FocusSource) => void;
  onFocus?: (type: FocusSource) => void;
}

export const useComponentFocus = ({
  initialFocus = false,
  onBeforeRemove,
  onBlur,
  onFocus,
}: Options) => {
  const navigation = useNavigation();
  const shouldFireInitialFocusRef = useRef(initialFocus);

  // Listen to navigation events
  useEffect(() => {
    // react-navigation fires the focus event before the listener is added so
    // we manually fire it here
    if (shouldFireInitialFocusRef.current && navigation.isFocused()) {
      onFocus?.(FocusSource.Navigation);
      shouldFireInitialFocusRef.current = false;
    }

    const beforeRemoveListener = navigation.addListener('beforeRemove', () => {
      onBeforeRemove?.();
    });

    const removeBlurListener = navigation.addListener('blur', () => {
      onBlur?.(FocusSource.Navigation);
    });

    const removeFocusListener = navigation.addListener('focus', () => {
      onFocus?.(FocusSource.Navigation);

      // Set to false in case the `useEffect` hook runs again
      shouldFireInitialFocusRef.current = false;
    });

    return () => {
      beforeRemoveListener();
      removeBlurListener();
      removeFocusListener();
    };
  }, [initialFocus, navigation, onBeforeRemove, onBlur, onFocus]);

  // Listen to AppState events
  useEffect(() => {
    const callback = (status: AppStateStatus) => {
      // Check if there's navigation focus, otherwise AppState changes will
      // always execute the callbacks as long as the component is mounted
      if (!navigation.isFocused()) {
        return;
      }

      // iOS also has an `inactive` state that's set when the app transitions
      // between foreground and background, but we can ignore it to prevent
      // executing the callbacks multiple times
      switch (status) {
        case 'active':
          onFocus?.(FocusSource.App);
          break;
        case 'background':
          onBlur?.(FocusSource.App);
          break;
      }
    };

    const changeEvent = AppState.addEventListener('change', callback);
    return () => {
      changeEvent.remove();
    };
  }, [navigation, onBlur, onFocus]);
};
